import koalaPicture from "../../assets/HomePage/Facts-photo.jpeg";
import { ReactComponent as NameIcon } from "../../assets/HomePage/name-icon.svg";
import { ReactComponent as HabitatsIcon } from "../../assets/HomePage/habitats-icon.svg";
import { ReactComponent as WeightIcon } from "../../assets/HomePage/weight-icon.svg";
import { ReactComponent as LengthIcon } from "../../assets/HomePage/length-icon.svg";

export default function Facts() {
  return (
    <section className="custom-section">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-5xl font-extrabold text-gray-900 mb-8 text-center">
          FACTS
        </h2>

        <div className="flex flex-col lg:flex-row gap-10">
          {/* === Left Column: Main Text === */}
          <div className="lg:w-2/3 text-gray-700 text-justify space-y-4 text-[18px]/6">
            <p>
              The koala (<em className="font-semibold">Phascolarctos cinereus</em>) is a tree-dwelling, herbivorous marsupial <span className="font-semibold">native to Australia</span>. It's the only living member of the family Phascolarctidae and is most closely related to wombats. Koalas are found along the <span className="font-semibold">eastern and southern coasts</span>, including <span className="font-semibold">Queensland, New South Wales, Victoria</span>, and <span className="font-semibold">South Australia</span>.
            </p>
            <p>
              They typically inhabit <span className="font-semibold">Eucalyptus woodlands</span>, relying heavily on eucalyptus leaves for food. This diet is low in nutrients and contains toxins that most mammals cannot tolerate. Koalas are <span className="font-semibold">mostly sedentary</span>, sleeping up to <span className="font-semibold">20 hours a day</span>.
            </p>
            <p>
              A <span className="font-semibold">2017 WWF report</span> showed a <span className="font-semibold">53% decline</span> per generation in <span className="font-semibold">Queensland</span>, and <span className="font-semibold">26%</span> in <span className="font-semibold">New South Wales</span>. While populations in <span className="font-semibold">South Australia</span> and <span className="font-semibold">Victoria</span> appear stable, the <span className="font-semibold">Australian Koala Foundation (AKF)</span> challenged the idea of abundance, estimating fewer than <span className="font-semibold">100,000 koalas</span> as of <span className="font-semibold">2012</span>.
            </p>
            <p>
              In <span className="font-semibold">2022</span>, AKF estimated between <span className="font-semibold">43,000 and 100,000</span> koalas remain—down from an estimated <span className="font-semibold">8–10 million</span> in the early <span className="font-semibold">20th century</span>. The <span className="font-semibold">Australian Government</span> reported a population of <span className="font-semibold">92,000</span> in <span className="font-semibold">2021</span>, nearly halved since <span className="font-semibold">2001</span>.
            </p>
            <p>
              Koalas are listed as <span className="font-semibold">vulnerable</span> by the <span className="font-semibold">IUCN</span>. Major threats include <span className="font-semibold">habitat destruction</span>, <span className="font-semibold">urbanisation</span>, <span className="font-semibold">droughts</span>, and <span className="font-semibold">bushfires</span>, many intensified by <span className="font-semibold">climate change</span>. In <span className="font-semibold">February 2022</span>, the koala was officially listed as <span className="font-semibold">endangered</span> in the <span className="font-semibold">ACT, NSW</span>, and <span className="font-semibold">QLD</span>.
            </p>
          </div>

          {/* === Right Column: Image + Info Card === */}
          <div className="lg:w-1/3 flex flex-col gap-6 items-center">
            {/* Koala Image */}
            <div className="w-full h-64 bg-gray-200 rounded-xl overflow-hidden shadow-md">
              <img
                src={koalaPicture}
                alt="Koala"
                className="object-cover w-full h-full"
              />
            </div>

            {/* Info Card */}
            <div className="w-full bg-secondary border border-accent rounded-lg shadow-md p-4">
              <div className="grid grid-cols-2 gap-4 items-start">
                {/* VU Status */}
                <div className="flex items-start gap-3">
                  <div className="bg-orange-600 text-white font-bold text-sm w-8 h-8 flex items-center justify-center rounded-full mt-1">
                    VU
                  </div>
                  <div>
                    <p className="text-sm text-accentAlt font-semibold">STATUS</p>
                    <p className="text-sm text-orange-600 ">Vulnerable</p>
                  </div>
                </div>

                {/* EN Status */}
                <div className="flex items-start gap-3">
                  <div className="bg-orange-700 text-white font-bold text-sm w-8 h-8 flex items-center justify-center rounded-full mt-1">
                    EN
                  </div>
                  <div>
                    <p className="text-sm text-accentAlt font-semibold">STATUS</p>
                    <p className="text-sm text-orange-700 leading-tight">Endangered</p>
                    <p className="text-[11px] text-orange-700 leading-tight">(In ACT NSW QLD)</p>
                  </div>
                </div>

                {/* Scientific Name */}
                <div className="flex items-start gap-3">
                  <NameIcon className="w-6 h-6 text-accentAlt" />
                  <div>
                    <p className="text-sm text-accentAlt font-semibold">SCIENTIFIC NAME</p>
                    <p className="italic text-xs text-gray-700">Phascolarctos cinereus</p>
                  </div>
                </div>

                {/* Weight */}
                <div className="flex items-start gap-3">
                  <WeightIcon className="w-6 h-6 text-accentAlt" />
                  <div>
                    <p className="text-sm text-accentAlt font-semibold">WEIGHT</p>
                    <p className="text-sm text-gray-700">9–14 kg</p>
                  </div>
                </div>

                {/* Habitats */}
                <div className="flex items-start gap-3">
                  <HabitatsIcon className="w-6 h-6 text-accentAlt" />
                  <div>
                    <p className="text-sm text-accentAlt font-semibold">HABITATS</p>
                    <p className="text-sm text-gray-700">Eucalyptus forests</p>
                  </div>
                </div>

                {/* Length */}
                <div className="flex items-start gap-3">
                  <LengthIcon className="w-6 h-6 text-accentAlt" />
                  <div>
                    <p className="text-sm text-accentAlt font-semibold">LENGTH</p>
                    <p className="text-sm text-gray-700">60–85 cm</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}